'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import { isFulfilled, isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { SEARCH_KEY } from 'ecosystem-marketplace-core/constants/reviews/ReviewsListQueries';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import ReviewsListStats from 'ecosystem-marketplace-core/components/reviews/ReviewsListStats';
import ReviewsHeader from 'ecosystem-marketplace-core/components/reviews/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'ecosystem-marketplace-core/components/reviews/ReviewsFilterSortDropdowns';
import ReviewsPaginatedList from 'ecosystem-marketplace-core/components/reviews/ReviewsPaginatedList';
import { fetchReviewability, fetchReviews, fetchReviewStats, initializeReviewFilters, resetReviewabilityStatus, resetReviewsListStatus, resetReviewStatsStatus, updateReviewFilters } from '../../actions/ReviewsActions';
import { getReviewabilityRequestState, getReviewability, getReviewFilters, getReviewsList, getReviewsListRequestState, getReviewStats, getReviewStatsRequestState, getReviewsTotal } from '../../selectors/reviews';
import { getIsInApp } from '../../selectors/app';
import AssetReviewRenderer from './AssetReviewRenderer';
import ReviewsListEmptyState from './ReviewsListEmptyState';
import ReviewWizard from './ReviewWizard';
import WriteReviewButton from './WriteReviewButton';
const REVIEWS_LIST_WIDTH = 750;

const Reviews = ({
  name,
  exampleUrl,
  isPreview,
  offeringId,
  offeringType,
  onTrackInteraction,
  providerName,
  onReviewsFulfilled,
  reviewType,
  supportUrl,
  thumbnailUrl,
  versionNumber
}) => {
  const {
    pathname
  } = useLocation();
  const isReviewLink = pathname.includes('/write-review');
  const dispatch = useDispatch();
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [searchTerm, setSearchTerm] = useState('');
  const reviewabilityRequestState = useSelector(getReviewabilityRequestState);
  const reviewability = useSelector(getReviewability);
  const reviewsListRequestState = useSelector(getReviewsListRequestState);
  const reviewsList = useSelector(getReviewsList);
  const reviewStatsRequestState = useSelector(getReviewStatsRequestState);
  const reviewStats = useSelector(getReviewStats);
  const reviewsTotal = useSelector(getReviewsTotal); // Based on current filters

  const reviewsCount = reviewStats.get('count'); // All reviews

  const reviewFilters = useSelector(getReviewFilters);
  const isInApp = useSelector(getIsInApp);
  const {
    maxWidth
  } = useResponsiveProps({
    maxWidth: {
      lg: REVIEWS_LIST_WIDTH
    }
  });
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'review'
    }));
  }, [onTrackInteraction]);
  useEffect(() => {
    dispatch(initializeReviewFilters(offeringId));
  }, [dispatch, offeringId]);
  useEffect(() => {
    if (isUninitialized(reviewsListRequestState) && reviewFilters.offeringId === offeringId) {
      dispatch(fetchReviews(reviewFilters.toSearchQuery(), reviewType));
    }
  }, [dispatch, offeringId, reviewFilters, reviewsListRequestState, reviewType]);
  useEffect(() => {
    if (isUninitialized(reviewStatsRequestState)) {
      dispatch(fetchReviewStats(offeringId, reviewType));
    }
  }, [dispatch, reviewStatsRequestState, offeringId, reviewType]);
  useEffect(() => {
    if (isInApp && isUninitialized(reviewabilityRequestState)) {
      dispatch(fetchReviewability(offeringId));
    }
  }, [dispatch, isInApp, reviewabilityRequestState, offeringId]);
  useEffect(() => {
    return () => {
      dispatch(resetReviewsListStatus());
      dispatch(resetReviewStatsStatus());
      dispatch(resetReviewabilityStatus());
    };
  }, [dispatch]);
  useEffect(() => {
    if (isFulfilled(reviewStatsRequestState) && isFulfilled(reviewsListRequestState)) {
      onReviewsFulfilled(true);
    }
  }, [reviewStatsRequestState, reviewsListRequestState, onReviewsFulfilled]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    if (textKey === SEARCH_KEY) {
      setSearchTerm(value);
    }

    dispatch(updateReviewFilters({
      [textKey.toLowerCase()]: value
    }, reviewType));
  }, [dispatch, reviewType, setSearchTerm]);
  const renderedWriteReviewButton = useMemo(() => /*#__PURE__*/_jsx(UIFlex, {
    justify: "end",
    children: /*#__PURE__*/_jsx(WriteReviewButton, {
      className: "m-y-4",
      disabled: isPreview,
      offeringId: offeringId,
      offeringType: offeringType,
      onClick: () => setWizardOpen(true),
      reviewability: reviewability
    })
  }), [isPreview, offeringId, offeringType, reviewability, setWizardOpen]);
  const renderedReviewsList = useMemo(() => /*#__PURE__*/_jsxs("div", {
    style: {
      maxWidth
    },
    children: [/*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      renderedButtonRow: renderedWriteReviewButton,
      reviewsCount: reviewStats.get('count'),
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
      onUpdateFilters: onUpdateFilters,
      ReviewRenderer: AssetReviewRenderer,
      reviews: reviewsList,
      reviewsFetchStatus: reviewsListRequestState,
      reviewFilters: reviewFilters,
      reviewsTotal: reviewsTotal,
      searchTerm: searchTerm,
      reviewType: reviewType
    })]
  }), [onUpdateFilters, maxWidth, renderedWriteReviewButton, reviewFilters, reviewsList, reviewStats, reviewsTotal, reviewsListRequestState, searchTerm, reviewType]);
  const renderedReviewsBody = useMemo(() => {
    if (reviewsCount > 2) {
      return /*#__PURE__*/_jsxs(UIGrid, {
        className: "m-top-8",
        children: [/*#__PURE__*/_jsx(UIGridItem, {
          size: {
            xs: 12,
            lg: 4
          },
          children: /*#__PURE__*/_jsx(LoadingWrapper, {
            requestStatus: reviewStatsRequestState,
            children: /*#__PURE__*/_jsx(ReviewsListStats, {
              onUpdateFilters: onUpdateFilters,
              reviewStats: reviewStats,
              reviewType: reviewType
            })
          })
        }), /*#__PURE__*/_jsx(UIGridItem, {
          size: {
            xs: 12,
            lg: 8
          },
          children: renderedReviewsList
        })]
      });
    } else if (reviewsCount && reviewsCount <= 2) {
      return renderedReviewsList;
    } else if (!reviewsCount) {
      return /*#__PURE__*/_jsx(ReviewsListEmptyState, {
        isPreview: isPreview,
        offeringId: offeringId,
        offeringType: offeringType,
        reviewability: reviewability,
        onWriteReviewClick: () => setWizardOpen(true)
      });
    }

    return null;
  }, [isPreview, offeringId, offeringType, onUpdateFilters, renderedReviewsList, reviewability, reviewsCount, reviewStats, reviewStatsRequestState, reviewType]);
  const renderedWizard = useMemo(() => {
    return /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: reviewabilityRequestState,
      children: /*#__PURE__*/_jsx(ReviewWizard, {
        name: name,
        thumbnailUrl: thumbnailUrl,
        offeringId: offeringId,
        offeringType: offeringType,
        onClose: () => setWizardOpen(false),
        onSuccess: () => setWizardOpen(false),
        onTrackInteraction: trackInteraction,
        providerName: providerName,
        reviewability: reviewability,
        reviewType: reviewType,
        supportUrl: supportUrl,
        slug: exampleUrl,
        versionNumber: versionNumber
      })
    });
  }, [exampleUrl, name, offeringId, offeringType, providerName, reviewability, reviewabilityRequestState, supportUrl, thumbnailUrl, trackInteraction, versionNumber, reviewType]);
  return /*#__PURE__*/_jsxs("div", {
    style: {
      position: 'relative'
    },
    children: [/*#__PURE__*/_jsx(H2, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.reviews.title",
        options: {
          name
        }
      })
    }), renderedReviewsBody, wizardOpen && renderedWizard]
  });
};

Reviews.propTypes = {
  name: PropTypes.string,
  exampleUrl: PropTypes.string.isRequired,
  isPreview: PropTypes.bool,
  offeringId: PropTypes.number.isRequired,
  offeringType: PropTypes.oneOf(['MODULE', 'THEME']).isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  providerName: PropTypes.string.isRequired,
  onReviewsFulfilled: PropTypes.func,
  reviewType: PropTypes.oneOf(Object.values(ReviewTypes)).isRequired,
  supportUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string.isRequired,
  versionNumber: PropTypes.number.isRequired
};
export default Reviews;